import React from "react";
import { TriangleAlertIcon } from "lucide-react";
import { BaseSettingsTable } from "../components/BaseSettingsTable";
import { ColumnDef, CellContext } from "@tanstack/react-table";
import { GetBudgetResolutionEnum } from "@norma-bi/bi-api";
import { useGetBudget } from "@/hooks/useGetBudget";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { BudgetLoading } from "./BudgetLoading";
import { useT, type Translator } from "@/i18n";

type TableData = {
  venue_name: string;
  [key: string]: string | number;
};

type SettingsBudgetTableProps = {
  businessId: string;
  year: number;
  resolution: GetBudgetResolutionEnum;
};

/** Short month names in the interface language, January first. */
const monthNames = (locale: string) => {
  const format = new Intl.DateTimeFormat(locale, { month: "short", timeZone: "UTC" });
  return Array.from({ length: 12 }, (_, month) => format.format(Date.UTC(2000, month, 1)));
};

const generateWeeks = (count: number, { t, formatNumber }: Translator) => {
  return Array.from({ length: count }, (_, i) => t("budget.week", { week: formatNumber(i + 1) }));
};

// The branch column stays readable while the amounts line up at the end of their cells.
const BUDGET_TABLE_CLASS_NAME =
  "[&_th]:min-w-24 [&_th:first-child]:min-w-44 [&_td:not(:first-child)]:text-end [&_td:not(:first-child)]:tabular-nums [&_th:not(:first-child)]:text-end";

function BudgetTableError({ description }: { description: string }) {
  const { t } = useT();
  return (
    <Empty className="p-16">
      <EmptyHeader>
        <EmptyMedia variant="icon" className="bg-destructive/10 text-destructive">
          <TriangleAlertIcon />
        </EmptyMedia>
        <EmptyTitle className="text-destructive">{t("budget.error")}</EmptyTitle>
        <EmptyDescription>{description}</EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

const createColumns = (
  resolution: (typeof GetBudgetResolutionEnum)[keyof typeof GetBudgetResolutionEnum],
  translator: Translator,
): ColumnDef<TableData>[] => {
  const { t, locale, formatNumber } = translator;
  const baseColumn = [
    {
      header: t("budget.branch"),
      accessorKey: "venue_name",
      cell: ({ getValue }: CellContext<TableData, unknown>) => (
        <span dir="auto">{getValue() as string}</span>
      ),
    },
  ];

  const periods =
    resolution === GetBudgetResolutionEnum.weekly
      ? generateWeeks(52, translator)
      : monthNames(locale);

  const periodColumns = periods.map((period, index) => ({
    header: period,
    accessorKey: `period${index + 1}`,
    cell: ({ getValue }: CellContext<TableData, unknown>) =>
      formatNumber(getValue() as number, { maximumFractionDigits: 0 }),
  }));

  return [...baseColumn, ...periodColumns];
};

export function SettingsBudgetTable({ businessId, year, resolution }: SettingsBudgetTableProps) {
  const budget = useGetBudget(businessId, resolution, year);
  const translator = useT();
  const { t } = translator;

  const columns = React.useMemo(
    () => createColumns(resolution, translator),
    [resolution, translator],
  );

  const tableData = React.useMemo(() => {
    if (!budget.data) return [];
    return budget.data.venue_budgets.map((venue) => {
      const rowData: TableData = {
        venue_name: venue.venue_name,
      };
      venue.budget_values.forEach((value, index) => {
        rowData[`period${index + 1}`] = value;
      });
      return rowData;
    });
  }, [budget.data]);

  const dataPeriodsCount = budget.data?.venue_budgets[0]?.budget_values.length || 0;
  const columnsPeriodsCount = columns.length - 1;

  if (budget.isLoading) {
    return <BudgetLoading />;
  }

  if (budget.isError) {
    return <BudgetTableError description={t("budget.tryRefreshing")} />;
  }

  if (dataPeriodsCount !== columnsPeriodsCount) {
    return <BudgetLoading />;
  }

  if (!tableData || tableData.length === 0) {
    return <BudgetTableError description={t("budget.pleaseTryRefreshing")} />;
  }

  return (
    <BaseSettingsTable
      data={tableData}
      columns={columns}
      tableClassName={BUDGET_TABLE_CLASS_NAME}
    />
  );
}
